<template>
  <f7-page name="listing-submit" @page:show="onShow">
    <f7-navbar :title="$t('listingSubmit.title')" back-link no-shadow>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <div v-if="loading" class="text-align-center" style="padding: 32px 0">
          <f7-preloader />
        </div>

        <template v-else>
          <f7-block v-if="!listing" class="lst-error">
            <p>{{ $t('listingSubmit.noListing') }}</p>
            <f7-button small raised fill @click="goListings">{{ $t('listingSubmit.goListings') }}</f7-button>
          </f7-block>

          <f7-block v-else class="lst-card-wrap">
            <f7-card outline class="lst-listing-card">
              <div class="lst-listing-row">
                <img v-if="cover" class="lst-thumb" :src="cover" :alt="listing.title" />
                <span v-else class="lst-thumb lst-thumb-ph"><f7-icon f7="tag_fill" size="18" /></span>
                <div class="lst-listing-info">
                  <div class="lst-listing-title">{{ listing.title }}</div>
                  <div class="lst-listing-price">{{ formatPrice(listing) }}</div>
                  <span class="lst-badge" :class="`lst-badge--${listing.status}`">{{ statusLabel(listing.status) }}</span>
                </div>
              </div>
            </f7-card>

            <f7-block-title>{{ $t('listingSubmit.chooseGroup') }}</f7-block-title>
            <f7-list v-if="groups.length" inset strong>
              <f7-list-item
                v-for="g in groups"
                :key="g.id"
                :title="g.name"
                :subtitle="g.category"
                radio
                :name="'target-group'"
                :value="g.id"
                @change="selectedGroupId = $event.target.value"
              />
            </f7-list>
            <f7-block v-else class="text-align-center text-color-gray">
              {{ $t('listingSubmit.noGroups') }}
            </f7-block>

            <f7-list inset strong no-hairlines>
              <f7-list-input
                type="textarea"
                resizable
                :label="$t('listingSubmit.note')"
                :value="note"
                @input="note = $event.target.value"
                :placeholder="$t('listingSubmit.notePlaceholder')"
              />
            </f7-list>

            <f7-block>
              <f7-button large fill raised round :disabled="!canSubmit || submitting" @click="submit">
                {{ submitting ? $t('listingSubmit.sending') : $t('listingSubmit.submitAction') }}
              </f7-button>
              <p v-if="requiresReview" class="lst-hint">
                {{ $t('listingSubmit.requiresReview') }}
              </p>
            </f7-block>

            <f7-block v-if="submitted" class="lst-done">
              <f7-card outline class="lst-done-card">
                <p class="lst-done-text">{{ $t('listingSubmit.doneText') }}</p>
                <f7-button
                  v-if="lastGroupId"
                  small
                  fill raised round
                  @click="goMarketplace(lastGroupId)"
                >
                  {{ $t('listingSubmit.viewMarketplace') }}
                </f7-button>
              </f7-card>
            </f7-block>
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../common/AppLayout.vue";
import { useMarketplaceProductStore } from "../../js/pinia-stores/marketplaceProduct.js";
import { useMarketplaceStore } from "../../js/pinia-stores/marketplace.js";
import { useGroupsStore } from "../../js/pinia-stores/group.js";
import i18n from "../../js/i18n.js";

const props = defineProps({
  listingId: { type: String, default: "" },
});

const { t } = i18n.global;
const mpStore = useMarketplaceProductStore();
const marketplaceStore = useMarketplaceStore();
const groupsStore = useGroupsStore();

const loading = ref(true);
const listing = ref(null);
const groups = ref([]);
const selectedGroupId = ref("");
const note = ref("");
const submitting = ref(false);
const submitted = ref(false);
const lastGroupId = ref("");

const cover = computed(() => {
  const m = listing.value?.media?.[0];
  return m?.thumbnailUrl || m?.url || (typeof m === "string" ? m : "");
});

const requiresReview = computed(() =>
  ["DRAFT", "REJECTED", "EXPIRED"].includes(listing.value?.status),
);

const canSubmit = computed(
  () => !!listing.value && !!selectedGroupId.value && !submitting.value,
);

function statusLabel(status) {
  return (
    {
      DRAFT: t("sellerHub.statusDraft"),
      PENDING_REVIEW: t("sellerHub.statusPending"),
      ACTIVE: t("sellerHub.statusActive"),
      REJECTED: t("sellerHub.statusRejected"),
      EXPIRED: t("sellerHub.statusExpired"),
    }[status] || status
  );
}

function formatPrice(l) {
  if (l.price == null || l.price === "") return t("marketplace.priceOnRequest");
  const n = Number(l.price);
  return `${n.toLocaleString()}${l.currency ? " " + l.currency : ""}`;
}

async function load() {
  loading.value = true;
  try {
    const all = (await mpStore.myListings()) || [];
    listing.value = all.find((l) => String(l.id) === String(props.listingId)) || null;
  } catch (e) {
    listing.value = null;
  }
  try {
    if (!groupsStore.groups.length) await groupsStore.fetchGroups();
  } catch (e) {
    // directory unavailable
  }
  groups.value = groupsStore.groups || [];
  loading.value = false;
}

function onShow() {
  load();
}

async function submit() {
  if (!listing.value || !selectedGroupId.value) return;
  submitting.value = true;
  try {
    if (requiresReview.value) {
      await mpStore.submitListing(listing.value.id);
    }
    const ok = await marketplaceStore.submitToGroup(
      listing.value.id,
      selectedGroupId.value,
      note.value.trim(),
    );
    if (!ok) {
      f7.dialog.alert(t("listingSubmit.alreadySubmitted"), t("listingSubmit.title"));
      return;
    }
    lastGroupId.value = selectedGroupId.value;
    submitted.value = true;
    f7.toast.show({ text: t("listingSubmit.sent"), closeTimeout: 2500 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || t("listingSubmit.error"),
      t("listingSubmit.title"),
    );
  } finally {
    submitting.value = false;
  }
}

function goListings() {
  f7.views.main.router.navigate("/sell/listings");
}

function goMarketplace(groupId) {
  f7.views.main.router.navigate(`/groups/${groupId}/marketplace`);
}

onMounted(() => {
  load();
});
</script>

<style scoped>
.lst-error {
  text-align: center;
  padding: 24px 16px;
}
.lst-listing-card {
  border-radius: 12px;
}
.lst-listing-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.lst-thumb {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}
.lst-thumb-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  color: var(--f7-theme-color);
}
.lst-listing-info {
  flex: 1;
  min-width: 0;
}
.lst-listing-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.lst-listing-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
  margin-top: 2px;
}
.lst-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 4px;
}
.lst-badge--DRAFT { background: color-mix(in srgb, #8e8e93 12%, transparent); color: #636366; }
.lst-badge--PENDING_REVIEW { background: color-mix(in srgb, #b25e09 12%, transparent); color: #b25e09; }
.lst-badge--ACTIVE { background: color-mix(in srgb, #1a6b42 12%, transparent); color: #1a6b42; }
.lst-hint {
  font-size: 12px;
  color: color-mix(in srgb, #b25e09 90%, transparent);
  margin-top: 8px;
}
.lst-done-card {
  border-radius: 12px;
  background: color-mix(in srgb, #1a6b42 8%, transparent);
}
.lst-done-text {
  font-size: 14px;
  font-weight: 600;
  color: #1a6b42;
}
</style>